import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  Box,
  Card,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { HeaderBreadcrumbs, Page, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { TableHeadCustom, TableNoData, TableRowLink } from '@/components/table';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable from '@/hooks/useTable';
import { PATH_USER } from '@/features/app';
import {
  getInvoiceDryRuns,
  GetInvoiceDryRunsParams,
} from '@/features/invoices/api';
import { InvoiceDryRunStatusLabel } from '../components';
import { invoicesPath } from '../routes/paths';
import {
  formatDryRunCount,
  formatDryRunDateRange,
  formatDryRunDateTime,
  formatRequestedBy,
  INVOICE_DRY_RUN_STATUSES,
  INVOICE_DRY_RUNS_TABLE_HEAD,
} from '../utils';
import { InvoiceDryRunStatus } from '../types';

export default function InvoiceDryRunList() {
  const {
    dense,
    page,
    rowsPerPage,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
    setPage,
  } = useTable({ defaultRowsPerPage: 25 });
  const { themeStretch } = useSettings();
  const { errorMsg } = useSnackbarMsg();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<'All' | InvoiceDryRunStatus>(
    'All'
  );

  const queryParams: GetInvoiceDryRunsParams = {
    page: page + 1,
    limit: rowsPerPage,
    ...(filterStatus !== 'All' ? { status: filterStatus } : {}),
  };

  const { data: dryRunsData, isLoading } = useQuery({
    queryKey: ['invoiceDryRuns', queryParams],
    queryFn: () => getInvoiceDryRuns(queryParams),
    keepPreviousData: true,
    onError: (error) => {
      errorMsg(error, 'Something went wrong while fetching dry runs');
    },
  });

  const dryRuns = dryRunsData?.data ?? [];
  const totalDryRuns = dryRunsData?.meta.total ?? dryRuns.length;
  const isNotFound = !dryRuns.length;

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterStatus(event.target.value as 'All' | InvoiceDryRunStatus);
    setPage(0);
  };

  return (
    <Page title="Invoice Dry Runs">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Invoice Dry Runs"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Invoice Dry Runs', href: '' },
          ]}
        />

        <Card>
          <Grid container spacing={2} sx={{ py: 2.5, px: 3 }}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filterStatus}
                onChange={handleStatusChange}
              >
                <MenuItem value="All">All</MenuItem>
                {INVOICE_DRY_RUN_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {isLoading ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 1050, position: 'relative' }}>
                  <Table size={dense ? 'small' : 'medium'}>
                    <TableHeadCustom headLabel={INVOICE_DRY_RUNS_TABLE_HEAD} />
                    <TableBody>
                      {dryRuns.map((dryRun) => (
                        <TableRow
                          key={dryRun.id}
                          hover
                          onClick={() =>
                            navigate(
                              invoicesPath('dryRunDetails', {
                                id: String(dryRun.id),
                              })
                            )
                          }
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell align="left">
                            <TableRowLink
                              linkText={dryRun.id}
                              linkTo={invoicesPath('dryRunDetails', {
                                id: String(dryRun.id),
                              })}
                            />
                          </TableCell>
                          <TableCell align="left">
                            {formatDryRunDateRange(
                              dryRun.date_from,
                              dryRun.date_to
                            )}
                          </TableCell>
                          <TableCell align="left">
                            {formatRequestedBy(dryRun.requested_by)}
                          </TableCell>
                          <TableCell align="left">
                            <InvoiceDryRunStatusLabel status={dryRun.status} />
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunCount(
                              dryRun.total_jobs,
                              dryRun.status
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunCount(
                              dryRun.processed_jobs,
                              dryRun.status
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunCount(
                              dryRun.issue_count,
                              dryRun.status
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatDryRunCount(
                              dryRun.provider_invoice_count,
                              dryRun.status
                            )}
                          </TableCell>
                          <TableCell align="left">
                            {formatDryRunDateTime(dryRun.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}

                      <TableNoData
                        isNotFound={isNotFound}
                        title="No invoice dry runs found"
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100, 150]}
                  component="div"
                  count={totalDryRuns}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={onChangePage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={<Switch checked={dense} onChange={onChangeDense} />}
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
